import { Card, Header, Spacer } from '@procurify/ui'

import {
  formatAccountCodeAttribute,
  formatSecondaryOrgCategoryAttribute,
  formatViewersAttribute,
} from '@webapp/components/Budgets/SpendTracker/utils'
import { useFeatureFlag, useProcurifyIntl } from '@webapp/hooks'
import {
  type AccountCode,
  type BudgetCategoryCustomField,
  type Department,
  type User,
} from '@webapp/models'
import { CustomFieldTypes } from '@webapp/models/enums'
import messages from './messages'
import { BudgetCategoryAttributeDescriptions } from '../BudgetCategoryAttributeDescriptions'
import { BudgetCategoryAttributesCardStyled } from './styles/BudgetCategoryAttributesCardStyled'

const CHECKBOX_CHECKED_VALUE = 'true'

export interface IBudgetCategoryAttributesCardProps {
  header?: string
  departments: Department[]
  accountCodes: AccountCode[]
  viewers: User[]
  customFields?: BudgetCategoryCustomField[]
  isSourceOfTruth?: boolean
}

export const BudgetCategoryAttributesCard = ({
  header,
  departments,
  accountCodes,
  viewers,
  customFields = [],
  isSourceOfTruth,
}: IBudgetCategoryAttributesCardProps) => {
  const intl = useProcurifyIntl()
  const PUR_4252_BUDGET_BY_CUSTOM_FIELDS = useFeatureFlag(
    'PUR_4252_BUDGET_BY_CUSTOM_FIELDS'
  )

  const formatBudgetCategoryAttributes = (
    _departments,
    _accountCodes,
    _viewers
  ) => {
    return [
      {
        label: intl.formatMessage(messages.secondaryOrgCategoryLabel),
        value: formatSecondaryOrgCategoryAttribute(_departments),
      },
      {
        label: intl.formatMessage(messages.accountCodesLabel),
        value: formatAccountCodeAttribute(_accountCodes),
      },
      {
        label: intl.formatMessage(messages.viewersLabel),
        value: formatViewersAttribute(_viewers),
      },
    ]
  }

  const formatCustomFieldValue = (
    customField: BudgetCategoryCustomField,
    value: string
  ) => {
    if (customField.field_type !== CustomFieldTypes.CHECKBOX) return value
    if (value.toLowerCase() === CHECKBOX_CHECKED_VALUE) {
      return intl.formatMessage(messages.checkboxCheckedValue)
    } else {
      return intl.formatMessage(messages.checkboxUncheckedValue)
    }
  }

  const formatCustomFieldAttributes = () =>
    customFields.map((customField) => ({
      label: customField.is_active
        ? customField.name
        : intl.formatMessage(messages.inactiveCustomFieldLabel, {
            name: customField.name,
          }),
      value: customField.values.map((value) =>
        formatCustomFieldValue(customField, value)
      ),
    }))

  const [secondaryOrgCategoryAttribute, accountCodeAttribute, viewerAttribute] =
    formatBudgetCategoryAttributes(departments, accountCodes, viewers)

  return (
    <BudgetCategoryAttributesCardStyled>
      <Card>
        <Card.Content>
          <Header as='h5'>
            {header || intl.formatMessage(messages.budgetAttributesCardHeader)}
          </Header>
          <Spacer space={1} />
          {PUR_4252_BUDGET_BY_CUSTOM_FIELDS ? (
            <BudgetCategoryAttributeDescriptions
              attributes={[
                secondaryOrgCategoryAttribute,
                accountCodeAttribute,
                ...formatCustomFieldAttributes(),
              ]}
              secondaryAttributes={[viewerAttribute]}
              isSourceOfTruth={isSourceOfTruth}
            />
          ) : (
            <BudgetCategoryAttributeDescriptions
              attributes={[
                secondaryOrgCategoryAttribute,
                accountCodeAttribute,
                viewerAttribute,
              ]}
              isSourceOfTruth={isSourceOfTruth}
            />
          )}
        </Card.Content>
      </Card>
    </BudgetCategoryAttributesCardStyled>
  )
}

BudgetCategoryAttributesCard.displayName = 'BudgetCategoryAttributesCard'
